/**
 * MeshProvider — owns the Trystero peer + roster lifecycle for a subtree.
 *
 * Mounts a single `Peer` from `@unstable-legion/core`'s `joinMesh()` on
 * first render, tears it down on unmount. Children pull the peer handle
 * via `useMeshContext()`; hooks (`useMeshRoster`, `useMeshChat`)
 * subscribe to the underlying event sources.
 *
 * The host app picks the Trystero strategy by importing `joinRoom`
 * from `'trystero/torrent'` (or `/ipfs`, `/nostr`, `/mqtt`) and passing
 * it in. We don't bundle a strategy because the relay choice depends
 * on the deployment context.
 */
import { createContext, createElement, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { joinMesh, } from '@unstable-legion/core';
const MeshContext = createContext({ peer: null });
/**
 * Wrap the part of the tree that needs mesh access. One provider per
 * room — nesting providers gives each subtree its own mesh.
 */
export function MeshProvider(props) {
    const { joinRoom, selfId, trysteroConfig, roomId, cap, heartbeatMs, children, } = props;
    const [peer, setPeer] = useState(null);
    const peerRef = useRef(null);
    useEffect(() => {
        const initialCap = { ...cap, ts: cap.ts ?? Date.now() };
        const p = joinMesh({
            joinRoom,
            selfId,
            trysteroConfig,
            roomId,
            cap: initialCap,
            heartbeatMs,
        });
        peerRef.current = p;
        setPeer(p);
        return () => {
            p.leave();
            peerRef.current = null;
            setPeer(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [joinRoom, selfId, roomId, heartbeatMs, JSON.stringify(trysteroConfig)]);
    // Re-broadcast on cap change (without recreating the peer).
    const stableCapKey = JSON.stringify(cap);
    useEffect(() => {
        if (!peerRef.current)
            return;
        peerRef.current.setCap({ ...cap, ts: cap.ts ?? Date.now() });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableCapKey]);
    const value = useMemo(() => ({ peer }), [peer]);
    return createElement(MeshContext.Provider, { value }, children);
}
/**
 * Pull the current mesh context. Returns `{ peer: null }` until the
 * Trystero room has connected; downstream hooks tolerate that null.
 */
export function useMeshContext() {
    return useContext(MeshContext);
}
